import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  disciplineTypeLabelKeys,
  DisciplineType,
  NonAttendanceType,
  type NonAttendance,
} from '@/types/overview';
import { tv } from '@/utils/tv';

interface Props {
  nonAttendance: NonAttendance;
  onClick?: () => void;
}

const valueBadge = tv({
  base: 'text-body-13 flex h-[26px] min-w-6 items-center justify-center rounded-xl px-2 font-medium tracking-[0.025em] text-foreground',
  variants: {
    isExcused: {
      true: 'bg-on-primary-surface',
      false: 'bg-red-soft',
    },
  },
});

export const AbsenceRow: React.FC<Props> = ({ nonAttendance, onClick }) => {
  const { t } = useTranslation();
  const { disciplineType, type } = nonAttendance;
  const { Sick, Other } = NonAttendanceType;
  const isExcused = type === Sick || type === Other;
  const isMandatoryContent = disciplineType === DisciplineType.MandatoryContent;
  const disciplineTypeLabelKey = disciplineTypeLabelKeys[disciplineType];
  const hasDisciplineTypeLabel =
    !isMandatoryContent && !!disciplineTypeLabelKey;

  const content = (
    <>
      <span className="flex min-w-0 flex-1 items-center gap-2">
        <span className="text-body-14 truncate text-foreground">
          {nonAttendance.disciplineName}
        </span>
        {hasDisciplineTypeLabel && (
          <span className="text-caption-12 bg-on-primary-surface shrink-0 rounded-xl px-2 py-0.5 text-foreground">
            {t(disciplineTypeLabelKey)}
          </span>
        )}
      </span>
      <div className="relative flex shrink-0 items-center">
        <span className={valueBadge({ isExcused })}>{nonAttendance.value}</span>
        {isExcused && (
          <span className="text-caption-10 bg-blue-soft text-foreground absolute -top-1.5 -right-1.5 flex size-4 items-center justify-center rounded-full leading-none">
            {nonAttendance.type}
          </span>
        )}
      </div>
    </>
  );

  if (!onClick) {
    return (
      <li className="flex items-center justify-between gap-3 px-4 py-3 md:px-5">
        {content}
      </li>
    );
  }

  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="flex w-full cursor-pointer items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-hover md:px-5"
      >
        {content}
      </button>
    </li>
  );
};
